import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus, Trophy } from "lucide-react";

interface EloNotificationProps {
  oldElo: number;
  newElo: number;
  change: number;
  coinsAwarded: number;
  gemsAwarded: number;
  visible: boolean;
  onClose: () => void;
}

export function EloNotification({ oldElo, newElo, change, coinsAwarded, gemsAwarded, visible, onClose }: EloNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showRewards, setShowRewards] = useState(false);

  useEffect(() => {
    if (visible) {
      setIsVisible(true);
      setTimeout(() => setShowRewards(true), 300);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setShowRewards(false);
        setTimeout(onClose, 400);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);

  if (!visible) return null;

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-success';
    if (change < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) return <TrendingUp className="h-5 w-5" />;
    if (change < 0) return <TrendingDown className="h-5 w-5" />;
    return <Minus className="h-5 w-5" />;
  };

  const getBgGradient = (change: number) => {
    if (change > 0) return 'from-success/20 to-background';
    if (change < 0) return 'from-destructive/20 to-background';
    return 'from-muted/20 to-background';
  };

  return (
    <div className={`fixed top-20 right-4 z-50 transition-all duration-500 ${
      isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
    }`}>
      <Card className={`border-2 border-accent/50 bg-gradient-to-br ${getBgGradient(change)} backdrop-blur-lg shadow-2xl min-w-[280px]`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className={`flex items-center gap-2 ${getChangeColor(change)}`}>
              {getChangeIcon(change)}
              <span className="font-bold text-lg">Rating Update</span>
            </div>
            <Trophy className="h-5 w-5 text-primary ml-auto" />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Rating Change</span>
              <Badge variant="outline" className={`text-base font-bold ${getChangeColor(change)}`}>
                {oldElo} → {newElo}
              </Badge>
            </div>
            
            <div className={`text-2xl font-bold text-center py-2 ${getChangeColor(change)}`}>
              {change > 0 ? '+' : ''}{change} points
            </div>

            {showRewards && (coinsAwarded !== 0 || gemsAwarded !== 0) && (
              <div className="pt-3 border-t border-border/50 space-y-1 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="text-xs text-muted-foreground mb-1">Rewards Earned</div>
                {coinsAwarded !== 0 && (
                  <div className={`flex items-center justify-between text-sm ${coinsAwarded > 0 ? 'text-success' : 'text-destructive'}`}>
                    <span>Coins</span>
                    <span className="font-bold">{coinsAwarded > 0 ? '+' : ''}{coinsAwarded}</span>
                  </div>
                )}
                {gemsAwarded > 0 && (
                  <div className="flex items-center justify-between text-sm text-primary">
                    <span>Gems</span>
                    <span className="font-bold">+{gemsAwarded}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}